import { Image } from 'expo-image';
import { useLocalSearchParams, useRouter } from 'expo-router';
import type { RefObject } from 'react';
import { Pressable } from 'react-native';
import type { EnrichedTextInputInstance } from 'react-native-enriched-html';
import { StyleSheet, withUnistyles } from 'react-native-unistyles';
import {
  type ChatMessageActionSheetModal,
  useChatDetailContext,
} from '@/components/Chat/ChatDetailContext';
import useChatActionItems from '@/hooks/useChatActionItems';
import { LONG_PRESS_DELAY } from '@/utils/constants';
import handleLongPress from '@/utils/handleLongPress';

const UnistylesImage = withUnistyles(Image);
type ChatMediaRemoteItemProps = {
  url?: string | null;
};
const style = StyleSheet.create((theme) => ({
  image: {
    flex: 1,
    width: '100%',
    height: 240,
    maxWidth: 300,
  },
  pressed: {
    opacity: 0.7,
  },
  pressable: {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.xl,
    height: 240,
    marginVertical: theme.size(1),
    maxWidth: 300,
    overflow: 'hidden',
    width: '80%',
  },
}));
const IMAGE_TRANSITION_DURATION = 500;
function presentRemoteActionSheet({
  bottomSheetRef,
  getActionItems,
  inputRef,
  url,
}: {
  bottomSheetRef: RefObject<ChatMessageActionSheetModal | null>;
  getActionItems: ReturnType<typeof useChatActionItems>;
  inputRef: RefObject<EnrichedTextInputInstance | null>;
  url: string;
}) {
  inputRef.current?.blur();
  bottomSheetRef.current?.present(getActionItems('', url, '', false));
}
export default function ChatMediaRemoteItem({
  url = null,
}: ChatMediaRemoteItemProps) {
  const getActionItems = useChatActionItems();
  const { push } = useRouter();
  const { chatId } = useLocalSearchParams();
  const bottomSheetRef = useChatDetailContext((state) => state.bottomSheetRef);
  const inputRef = useChatDetailContext((state) => state.inputRef);
  const onPress = () => {
    push({
      pathname: '/chat/[chatId]/media',
      params: {
        chatId,
        url,
      },
    });
  };
  const onLongPress = () => {
    if (url) {
      presentRemoteActionSheet({
        bottomSheetRef,
        getActionItems,
        inputRef,
        url,
      });
    }
  };
  return (
    <Pressable
      onPress={onPress}
      onLongPress={handleLongPress(onLongPress)}
      delayLongPress={LONG_PRESS_DELAY}
      style={({ pressed }) => [style.pressable, pressed && style.pressed]}
    >
      <UnistylesImage
        transition={IMAGE_TRANSITION_DURATION}
        contentFit="cover"
        source={url}
        style={style.image}
      />
    </Pressable>
  );
}
